"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.measureText = void 0;
const wordCache = new Map();
const takeMeasurement = ({ text, fontFamily, fontSize, fontWeight, letterSpacing, fontVariantNumeric, additionalStyles, textTransform, }) => {
    if (typeof document === 'undefined') {
        throw new Error('measureText() can only be called in a browser.');
    }
    const node = document.createElement('span');
    if (fontFamily) {
        node.style.fontFamily = fontFamily;
    }
    node.style.display = 'inline-block';
    node.style.position = 'absolute';
    node.style.top = `-10000px`;
    node.style.whiteSpace = 'pre';
    node.style.fontSize =
        typeof fontSize === 'string' ? fontSize : `${fontSize}px`;
    if (additionalStyles) {
        for (const key of Object.keys(additionalStyles)) {
            node.style[key] = additionalStyles[key];
        }
    }
    if (fontWeight) {
        node.style.fontWeight = fontWeight.toString();
    }
    if (letterSpacing) {
        node.style.letterSpacing = letterSpacing;
    }
    if (fontVariantNumeric) {
        node.style.fontVariantNumeric = fontVariantNumeric;
    }
    if (textTransform) {
        node.style.textTransform = textTransform;
    }
    node.innerText = text;
    document.body.appendChild(node);
    const computedFontFamily = window.getComputedStyle(node).fontFamily;
    const boundingBox = node.getBoundingClientRect();
    document.body.removeChild(node);
    return {
        boundingBox,
        computedFontFamily,
    };
};
/*
 * @description Calculates the width and height of specified text to be used for layout calculations. Only works in the browser, not in Node.js or Bun.
 * @see [Documentation](https://remotion.dev/docs/layout-utils/measure-text)
 */
const measureText = ({ text, fontFamily, fontSize, fontWeight, letterSpacing, fontVariantNumeric, validateFontIsLoaded, additionalStyles, textTransform, }) => {
    const key = `${text}-${fontFamily}-${fontWeight}-${fontSize}-${letterSpacing}-${textTransform}-${JSON.stringify(additionalStyles)}`;
    if (wordCache.has(key)) {
        return wordCache.get(key);
    }
    const { boundingBox, computedFontFamily } = takeMeasurement({
        fontFamily,
        fontSize,
        text,
        fontVariantNumeric,
        fontWeight,
        letterSpacing,
        additionalStyles,
        textTransform,
    });
    if (validateFontIsLoaded && text.trim().length > 0) {
        const { boundingBox: boundingBoxOfFallbackFont, computedFontFamily: computedFallback, } = takeMeasurement({
            fontFamily: null,
            fontSize,
            text,
            fontVariantNumeric,
            fontWeight,
            letterSpacing,
            additionalStyles,
            textTransform,
        });
        const sameAsFallbackFont = boundingBox.height === boundingBoxOfFallbackFont.height &&
            boundingBox.width === boundingBoxOfFallbackFont.width;
        // Ensure there are at least 4 unique characters, with just a few, there is more likely to be a false positive
        if (sameAsFallbackFont &&
            computedFallback !== computedFontFamily &&
            new Set(text).size > 4) {
            const err = [
                `Called measureText() with "fontFamily": ${JSON.stringify(fontFamily)} but it looks like the font is not loaded at the time of calling.`,
                `A measurement with the fallback font ${computedFallback} was taken and had the same dimensions, indicating that the browser used the fallback font.`,
                'See https://remotion.dev/docs/layout-utils/best-practices for best practices.',
            ];
            throw new Error(err.join('\n'));
        }
    }
    const result = { height: boundingBox.height, width: boundingBox.width };
    wordCache.set(key, result);
    return result;
};
exports.measureText = measureText;
